/**
 * Role Summary Cards Component
 *
 * Displays dynamic cards for each participating role (Buyer, Seller, Lender, etc.)
 * showing total amounts owed/received with breakdown items.
 *
 * Only renders cards for roles that have activity (from getRolesInvolved filter).
 * Each card shows:
 * - Role title (e.g., "YOUR CASH NEEDED AT CLOSING")
 * - Total amount in large, prominent typography
 * - Breakdown items (Price, Loan, Costs, etc.)
 *
 * Props:
 * - order: NewOrderType - Full order object with accounting data
 *
 * Responsive Layout:
 * - Desktop (≥sm): 3 columns per row
 * - Mobile (<sm): 1 column per row
 */

import {
  Box,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NewOrderType } from 'types/order';
import { PartyRole } from 'types/closing-disclosure';
import { getRolesInvolved, calculateRoleTotal } from 'services/role-charge-filter';
import {
  calculateBuyerCashDue,
  calculateSellerProceeds,
  calculateEscrowTotal,
} from 'services/closing-disclosure-calculator';

const useStyles = makeStyles((theme: any) => ({
  container: {
    marginBottom: theme.spacing(3),
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    border: '1px solid',
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
    },
  },
  buyerCard: {
    borderLeftWidth: 4,
    borderLeftColor: theme.palette.info.main,
  },
  sellerCard: {
    borderLeftWidth: 4,
    borderLeftColor: theme.palette.success.main,
  },
  lenderCard: {
    borderLeftWidth: 4,
    borderLeftColor: theme.palette.warning.main,
  },
  titleCompanyCard: {
    borderLeftWidth: 4,
    borderLeftColor: theme.palette.primary.main,
  },
  roleTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
  },
  amountContainer: {
    marginBottom: theme.spacing(2),
  },
  amount: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  breakdownLabel: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    letterSpacing: '0.3px',
  },
  breakdownItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.divider,
    '&:last-child': {
      borderBottomWidth: 0,
    },
  },
  breakdownItemLabel: {
    color: theme.palette.text.secondary,
    flex: 1,
  },
  breakdownItemValue: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(1),
  },
  gridItem: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

interface RoleSummaryCardsProps {
  order: NewOrderType | null | undefined;
}

/**
 * Format currency value in cents to USD string
 * e.g., 12902700 → "$129,027.00"
 */
function formatCurrency(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Get color class for card based on role
 */
function getCardClass(classes: any, role: PartyRole): string {
  switch (role) {
    case PartyRole.BUYER:
      return classes.buyerCard;
    case PartyRole.SELLER:
      return classes.sellerCard;
    case PartyRole.LENDER:
      return classes.lenderCard;
    case PartyRole.TITLE_COMPANY:
      return classes.titleCompanyCard;
    default:
      return '';
  }
}

/**
 * Get i18n key for role title header
 */
function getRoleTitleKey(role: PartyRole): string {
  switch (role) {
    case PartyRole.BUYER:
      return 'closingDisclosure.buyer-cash-header';
    case PartyRole.SELLER:
      return 'closingDisclosure.seller-proceeds-header';
    case PartyRole.LENDER:
      return 'closingDisclosure.lender-info-header';
    case PartyRole.TITLE_COMPANY:
      return 'closingDisclosure.title-company-header';
    case PartyRole.AGENT:
      return 'closingDisclosure.agent-header';
    default:
      return 'closingDisclosure.role-unknown';
  }
}

/**
 * RoleCard - Individual card component for one role
 */
interface RoleCardProps {
  role: PartyRole;
  total: number; // in cents
  order: NewOrderType | null | undefined;
  classes: any;
  t: any;
}

const RoleCard = ({ role, total, order, classes, t }: RoleCardProps) => {
  const titleKey = getRoleTitleKey(role);
  const cardClass = getCardClass(classes, role);

  // Build breakdown items based on role
  const breakdownItems = useMemo(() => {
    const items: Array<{ label: string; value: string }> = [];

    if (role === PartyRole.BUYER && order) {
      const buyerCash = calculateBuyerCashDue(order);
      const escrow = calculateEscrowTotal(order);

      if (buyerCash > 0) {
        items.push({
          label: t('closingDisclosure.breakdown-buyer-costs'),
          value: formatCurrency(buyerCash),
        });
      }
      if (escrow.total_cents > 0) {
        items.push({
          label: t('closingDisclosure.breakdown-escrow'),
          value: formatCurrency(escrow.total_cents),
        });
      }
    } else if (role === PartyRole.SELLER && order) {
      const sellerProceeds = calculateSellerProceeds(order);
      if (sellerProceeds > 0) {
        items.push({
          label: t('closingDisclosure.breakdown-net-proceeds'),
          value: formatCurrency(sellerProceeds),
        });
      }
    } else if (role === PartyRole.LENDER && order) {
      const loanAmount = calculateRoleTotal(order, PartyRole.LENDER, 'payee');
      const escrow = calculateEscrowTotal(order);

      if (loanAmount > 0) {
        items.push({
          label: t('closingDisclosure.breakdown-loan-amount'),
          value: formatCurrency(loanAmount),
        });
      }
      if (escrow.total_cents > 0) {
        items.push({
          label: t('closingDisclosure.breakdown-escrow-setup'),
          value: formatCurrency(escrow.total_cents),
        });
      }
    }

    return items;
  }, [role, order, t]);

  return (
    <Paper className={`${classes.card} ${cardClass}`}>
      <Box>
        <Typography className={classes.roleTitle}>
          {t(titleKey)}
        </Typography>

        <Box className={classes.amountContainer}>
          <Typography className={classes.amount}>
            {formatCurrency(total)}
          </Typography>
        </Box>

        {breakdownItems.length > 0 && (
          <Box>
            <Typography className={classes.breakdownLabel}>
              {t('closingDisclosure.breakdown-title')}
            </Typography>
            {breakdownItems.map((item) => (
              <Box key={item.label} className={classes.breakdownItem}>
                <Typography className={classes.breakdownItemLabel}>
                  {item.label}
                </Typography>
                <Typography className={classes.breakdownItemValue}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

/**
 * RoleSummaryCards - Main component
 *
 * Renders cards for all active roles in the transaction
 */
export const RoleSummaryCards = ({ order }: RoleSummaryCardsProps) => {
  const classes = useStyles();
  const { t } = useTranslation('closingDisclosure');

  // Get all roles that have activity
  const activeRoles = useMemo(() => {
    return getRolesInvolved(order);
  }, [order]);

  // Calculate totals for each role
  const roleTotals = useMemo(() => {
    const totals: Record<PartyRole, number> = {
      [PartyRole.BUYER]: calculateBuyerCashDue(order),
      [PartyRole.SELLER]: calculateSellerProceeds(order),
      [PartyRole.LENDER]: calculateRoleTotal(order, PartyRole.LENDER, 'payee'),
      [PartyRole.TITLE_COMPANY]: calculateRoleTotal(
        order,
        PartyRole.TITLE_COMPANY,
        'payee'
      ),
      [PartyRole.AGENT]: calculateRoleTotal(order, PartyRole.AGENT, 'payee'),
    };
    return totals;
  }, [order]);

  // Filter to only roles with activity AND non-zero totals
  const visibleRoles = useMemo(() => {
    return activeRoles.filter((role) => roleTotals[role] > 0);
  }, [activeRoles, roleTotals]);

  if (!order || visibleRoles.length === 0) {
    return null;
  }

  return (
    <Box className={classes.container}>
      <Grid container spacing={2}>
        {visibleRoles.map((role) => (
          <Grid item xs={12} sm={6} md={4} key={role} className={classes.gridItem}>
            <RoleCard
              role={role}
              total={roleTotals[role]}
              order={order}
              classes={classes}
              t={t}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RoleSummaryCards;
